import React from 'react';
import { Link, useHistory } from 'react-router-dom';
import { JIGSAW_PUZZLES, PUZZLE_CATEGORIES, DIFFICULTY_CONFIG, getDailyPuzzle } from '../../data/puzzles';
import { theme } from '../../styles/theme';
import { getUserProfile } from '../../services/userProfileService';

const PuzzleList: React.FC = () => {
    const history = useHistory();
    const dailyPuzzle = getDailyPuzzle();
    const user = getUserProfile();
    const completedPuzzleIds = user.stats.completedPuzzleIds;

    // Get 2 featured puzzles per category that aren't completed
    const getFeaturedForCategory = (categoryId: string) => {
        return JIGSAW_PUZZLES
            .filter(p => p.category === categoryId && !p.locked && !completedPuzzleIds.has(p.id))
            .slice(0, 2);
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: theme.gradients.nature,
            padding: '0',
            paddingBottom: '100px'
        }}>
            {/* Hero Section */}
            <div style={{
                background: 'transparent',
                padding: '24px 16px',
                textAlign: 'center',
                color: theme.colors.textPrimary
            }}>
                <h1 style={{
                    fontSize: '28px',
                    margin: '0 0 8px 0',
                    fontFamily: theme.fonts.display
                }}>
                    🧩 Puzzle Garden
                </h1>
                <p style={{
                    fontSize: '14px',
                    opacity: 0.9,
                    margin: '0',
                    color: theme.colors.textSecondary
                }}>
                    Cozy jigsaw puzzles for relaxation
                </p>
            </div>

            {/* Daily Puzzle Card */}
            <div style={{ padding: '0 16px', marginBottom: '24px' }}>
                <div
                    onClick={() => history.push(`/play/${dailyPuzzle.id}`)}
                    style={{
                        background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.secondary})`,
                        borderRadius: '20px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        boxShadow: theme.shadows.lg,
                    }}
                >
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        padding: '16px',
                    }}>
                        <img
                            src={dailyPuzzle.url}
                            alt={dailyPuzzle.title}
                            style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '12px',
                                objectFit: 'cover',
                            }}
                            crossOrigin="anonymous"
                        />
                        <div style={{ flex: 1 }}>
                            <div style={{
                                fontSize: '12px',
                                color: 'rgba(255,255,255,0.8)',
                                marginBottom: '4px',
                                fontWeight: 500,
                            }}>
                                🌅 TODAY'S PUZZLE
                            </div>
                            <div style={{
                                fontSize: '18px',
                                fontWeight: 700,
                                color: '#fff',
                                marginBottom: '4px',
                            }}>
                                {dailyPuzzle.title}
                            </div>
                            <div style={{
                                fontSize: '13px',
                                color: 'rgba(255,255,255,0.8)',
                            }}>
                                +{dailyPuzzle.xpReward * 2} XP bonus • {DIFFICULTY_CONFIG[dailyPuzzle.difficulty].pieces} pieces
                            </div>
                        </div>
                        <div style={{
                            fontSize: '24px',
                        }}>
                            ▶️
                        </div>
                    </div>
                </div>
            </div>

            {/* Categories with Featured Puzzles */}
            <div style={{ padding: '0 16px' }}>
                {PUZZLE_CATEGORIES.map(category => {
                    const puzzles = getFeaturedForCategory(category.id);

                    return (
                        <div key={category.id} style={{ marginBottom: '28px' }}>
                            {/* Category Header */}
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '12px',
                                }}
                            >
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}>
                                    <span style={{ fontSize: '24px' }}>{category.icon}</span>
                                    <span style={{
                                        fontSize: '18px',
                                        fontWeight: 600,
                                        color: theme.colors.textPrimary,
                                    }}>
                                        {category.name}
                                    </span>
                                </div>
                                <Link
                                    to={`/category/${category.id}`}
                                    style={{
                                        fontSize: '13px',
                                        color: theme.colors.primary,
                                        textDecoration: 'none',
                                        fontWeight: 500,
                                    }}
                                >
                                    View all →
                                </Link>
                            </div>

                            {/* Puzzle Cards */}
                            <div style={{
                                display: 'flex',
                                gap: '12px',
                                overflowX: 'auto',
                                paddingBottom: '8px',
                                scrollSnapType: 'x mandatory',
                            }}>
                                {puzzles.map(puzzle => {
                                    const config = DIFFICULTY_CONFIG[puzzle.difficulty];
                                    return (
                                        <div
                                            key={puzzle.id}
                                            onClick={() => history.push(`/play/${puzzle.id}`)}
                                            style={{
                                                minWidth: '160px',
                                                borderRadius: '16px',
                                                overflow: 'hidden',
                                                backgroundColor: theme.colors.cardBg,
                                                boxShadow: theme.shadows.md,
                                                cursor: 'pointer',
                                                scrollSnapAlign: 'start',
                                                transition: 'transform 0.2s',
                                            }}
                                        >
                                            <div style={{
                                                width: '100%',
                                                height: '120px',
                                                position: 'relative',
                                            }}>
                                                <img
                                                    src={puzzle.url}
                                                    alt={puzzle.title}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                    }}
                                                    crossOrigin="anonymous"
                                                />
                                                <div style={{
                                                    position: 'absolute',
                                                    bottom: '8px',
                                                    right: '8px',
                                                    padding: '4px 8px',
                                                    backgroundColor: config.color,
                                                    color: '#fff',
                                                    borderRadius: '6px',
                                                    fontSize: '11px',
                                                    fontWeight: 600,
                                                }}>
                                                    {config.pieces} pcs
                                                </div>
                                            </div>
                                            <div style={{ padding: '10px' }}>
                                                <div style={{
                                                    fontWeight: 600,
                                                    color: theme.colors.textPrimary,
                                                    fontSize: '14px',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}>
                                                    {puzzle.title}
                                                </div>
                                                <div style={{
                                                    fontSize: '12px',
                                                    color: theme.colors.textSecondary,
                                                    marginTop: '2px',
                                                }}>
                                                    ✨ {puzzle.xpReward} XP
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* View More Card */}
                                <Link
                                    to={`/category/${category.id}`}
                                    style={{
                                        minWidth: '100px',
                                        borderRadius: '16px',
                                        backgroundColor: category.color + '30',
                                        border: `2px dashed ${category.color}`,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        textDecoration: 'none',
                                        scrollSnapAlign: 'start',
                                    }}
                                >
                                    <span style={{ fontSize: '24px', marginBottom: '8px' }}>+</span>
                                    <span style={{
                                        fontSize: '12px',
                                        color: category.color,
                                        fontWeight: 500,
                                    }}>
                                        More
                                    </span>
                                </Link>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default PuzzleList;